import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, CheckCircle2, X, IdCard, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * KYC document uploader — accepts images + PDF up to 8 Mo.
 * Posts to /api/uploads/kyc and returns the storage path via onChange.
 */
export default function KycUpload({ value, onChange }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isPdf, setIsPdf] = useState(false);

  const pick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImg = file.type.startsWith("image/");
    const isPdfType = file.type === "application/pdf";
    if (!isImg && !isPdfType) {
      toast.error("Format non supporté (PNG, JPG, WEBP, HEIC ou PDF)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 8 Mo)");
      return;
    }
    setIsPdf(isPdfType);
    if (isImg) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    } else {
      setPreview("");
    }
    setFileName(file.name);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/kyc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Pièce d'identité envoyée");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Échec de l'envoi");
      setPreview("");
      setFileName("");
      onChange("");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setFileName("");
    setIsPdf(false);
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (value && (preview || isPdf)) {
    return (
      <div
        className="rounded-xl border-2 bg-white p-3 flex gap-3 items-start"
        style={{ borderColor: "var(--sl-primary)" }}
        data-testid="kyc-upload-done"
      >
        {preview ? (
          <img src={preview} alt="Pièce d'identité" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" />
        ) : (
          <div className="w-24 h-24 rounded-lg flex-shrink-0 bg-red-50 grid place-items-center text-red-600">
            <FileText size={28} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[var(--sl-primary)] text-sm font-medium">
            <CheckCircle2 size={14} /> Pièce envoyée
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">{fileName}</div>
          <button
            type="button"
            onClick={clear}
            className="text-xs text-red-600 hover:underline mt-2 inline-flex items-center gap-1"
            data-testid="kyc-upload-clear"
          >
            <X size={12} /> Remplacer
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        className="w-full rounded-xl border-2 border-dashed bg-white py-8 px-4 flex flex-col items-center justify-center gap-2 transition hover:border-[var(--sl-primary)] hover:bg-[var(--sl-bg-alt)] text-gray-500"
        style={{ borderColor: "var(--sl-border)" }}
        data-testid="kyc-upload"
      >
        {uploading ? (
          <>
            <div className="w-8 h-8 border-2 border-[var(--sl-primary)] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Envoi en cours…</span>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]">
              <Upload size={18} />
            </div>
            <span className="text-sm font-medium text-gray-700">
              <IdCard size={14} className="inline mr-1" />
              Téléverser votre pièce d'identité
            </span>
            <span className="text-xs text-gray-400">PNG, JPG, WEBP, HEIC ou PDF — max 8 Mo</span>
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf"
        className="hidden"
        onChange={handleFile}
      />
    </>
  );
}
